import { ConfirmationDialog } from 'components/ConfirmationDialog';
import { notificationsSlice } from 'features/notifications/notificationsSlice';
import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { Button, IconCopy } from 'uikit';
import { descriptionNumberAndDate } from 'utils/descriptionNumberAndDate';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { gqlCopyActKC2 } from '../workOrderQueries';

interface IActKC2CopyProps {
    actKc2Id?: number;
    number?: number | string | null;
    date?: Date | null;
    buttonNode?: any;
    hasNoButton?: boolean;
    isDisabled?: boolean;
    onCopy?: () => void;
    withCloseHelper?: (action: () => void) => () => void;
}

export const ActKC2Copy = ({
    actKc2Id,
    number,
    date,
    hasNoButton,
    buttonNode,
    onCopy,
    isDisabled,
    withCloseHelper,
}: IActKC2CopyProps) => {
    const dispatch = useDispatch();
    const variables = useMemo(() => ({ actKC2Id: actKc2Id }), [actKc2Id]);

    const [copyData, copyStatus, , copyActKC2, setData] = useRequest({
        requestId: 'copyActKC2',
        query: gqlCopyActKC2,
        variables,
        withEffect: false,
    });

    const [isConfirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
    const [isCommitted, setIsCommitted] = useState(false);

    const handleCopyBtnClick = useCallback(() => {
        setConfirmationDialogOpen(true);
    }, []);

    const handleDialogClose = useCallback(() => {
        if (withCloseHelper) {
            withCloseHelper(() => setConfirmationDialogOpen(false))();
        } else {
            setConfirmationDialogOpen(false);
        }
    }, [withCloseHelper]);

    const handleCopySubmit = useCallback(() => {
        setConfirmationDialogOpen(false);
        setIsCommitted(true);
        dispatch(copyActKC2(variables));
    }, [copyActKC2, dispatch, variables]);

    useEffect(() => {
        const newActKC2 = copyData?.uerCopyActKC2.data;

        if (copyStatus === RequestStatus.SUCCESS && newActKC2 && isCommitted) {
            const { number: newNumber, date: newDate, object } = newActKC2;

            dispatch(
                notificationsSlice.actions.add({
                    key: `copiedActKC2_${newActKC2.id}`,
                    message: `Создана копия Акта КС-2 
                    ${descriptionNumberAndDate(newNumber, newDate)}${
                        object?.name ? ` к Объекту «${object?.name}»` : ''
                    }`,
                    status: 'success',
                }),
            );
            dispatch(setData(undefined));
            setIsCommitted(false);
            onCopy?.();
        }
    }, [copyData, copyStatus, dispatch, setData, onCopy, isCommitted]);

    useEffect(() => {
        buttonNode?.addEventListener('click', handleCopyBtnClick);

        return () => buttonNode?.removeEventListener('click', handleCopyBtnClick);
    }, [buttonNode, handleCopyBtnClick]);

    return (
        <>
            {!hasNoButton && (
                <Button
                    view="secondary"
                    onlyIcon
                    iconRight={IconCopy}
                    iconSize="s"
                    size="s"
                    onClick={handleCopyBtnClick}
                    loading={copyStatus === RequestStatus.FETCHING}
                    disabled={isDisabled}
                />
            )}
            <ConfirmationDialog
                textHeader="Копирование акта"
                textConfirmation={`Будет создана копия Акта${descriptionNumberAndDate(number, date)}`}
                cancelButtonText="Отменить"
                confirmButtonText="Продолжить"
                isOpened={isConfirmationDialogOpen}
                onSubmit={handleCopySubmit}
                onClose={handleDialogClose}
            />
        </>
    );
};

ActKC2Copy.displayName = 'ActKC2Copy';
